// Endless mobile product gallery: a copy of the last photo sits before the first and a copy of the first after the last.
// When a swipe settles on a copy, the gallery jumps invisibly to the real photo, so the photos keep cycling in both directions.
// Mobile only: on desktop the gallery is a grid, so the copies are removed.
class GalleryLoop extends HTMLElement {
  #abort = null;
  #clones = [];
  #timer = 0;
  #anchor = null;
  #query = null;
  #resizeObserver = null;

  connectedCallback() {
    const scope = this.closest(this.dataset.scope || 'body') || document.body;
    this.target = scope.querySelector(this.dataset.target);
    if (!this.target || this.target.children.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.target.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    this.target.addEventListener('scrollend', () => this.#settle(), { signal });
    // The loop only runs on mobile; on desktop the gallery is a grid, so the copies are removed.
    this.#query = window.matchMedia('(width < 48rem)');
    this.#query.addEventListener('change', () => this.#sync(), { signal });
    this.#sync();
    // Slide widths change when styles load or the viewport resizes; keep the current photo aligned to the leading edge.
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#anchor) this.#jumpTo(this.#anchor);
    });
    this.#resizeObserver.observe(this.target);
    this.#slides.forEach((slide) => this.#resizeObserver.observe(slide));
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#abort?.abort();
    clearTimeout(this.#timer);
    this.#removeClones();
  }

  get #slides() {
    return [...this.target.children].filter((slide) => !slide.hasAttribute('data-loop-clone'));
  }

  #sync() {
    if (this.#query.matches) {
      if (!this.#clones.length) this.#addClones();
    } else {
      this.#removeClones();
    }
  }

  #makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-loop-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.classList.add('md:hidden');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));
    clone.querySelectorAll('[fetchpriority]').forEach((element) => element.removeAttribute('fetchpriority'));
    clone.querySelectorAll('video, iframe').forEach((media) => media.remove());
    return clone;
  }

  #addClones() {
    const slides = this.#slides;
    const first = slides[0];
    const last = slides[slides.length - 1];
    const before = this.#makeClone(last);
    const after = this.#makeClone(first);
    this.target.prepend(before);
    this.target.append(after);
    this.#clones = [before, after];
    this.target.dataset.loopOffset = '1';
    this.#anchor = first;
    this.#jumpTo(first);
  }

  #removeClones() {
    if (!this.#clones.length) return;
    const current = this.#currentSlide();
    this.#clones.forEach((clone) => clone.remove());
    this.#clones = [];
    delete this.target.dataset.loopOffset;
    if (current && !current.hasAttribute('data-loop-clone')) this.#jumpTo(current);
    this.target.dispatchEvent(new Event('scroll'));
  }

  // The current photo is the one snapped to the leading edge (slides snap to start, and several can be in view on desktop).
  #currentSlide() {
    const rtl = getComputedStyle(this.target).direction === 'rtl';
    const targetRect = this.target.getBoundingClientRect();
    const edge = rtl ? targetRect.right : targetRect.left;
    let closest = null;
    let distance = Infinity;
    for (const slide of this.target.children) {
      const rect = slide.getBoundingClientRect();
      const gap = Math.abs((rtl ? rect.right : rect.left) - edge);
      if (gap < distance) {
        distance = gap;
        closest = slide;
      }
    }
    return closest;
  }

  #jumpTo(slide) {
    // Slides share one width and snap to start, so a slide's scroll position is its index times one slide plus the gap.
    const index = [...this.target.children].indexOf(slide);
    const gap = parseFloat(getComputedStyle(this.target).columnGap) || 0;
    const rtl = getComputedStyle(this.target).direction === 'rtl';
    const left = index * (slide.getBoundingClientRect().width + gap) * (rtl ? -1 : 1);
    const previous = this.target.style.scrollSnapType;
    this.target.style.scrollSnapType = 'none';
    this.target.scrollTo({ left, behavior: 'instant' });
    this.target.style.scrollSnapType = previous;
  }

  // Fallback for browsers without scrollend: settle once scrolling has been quiet briefly.
  #onScroll() {
    if (!this.#clones.length || 'onscrollend' in window) return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#settle(), 150);
  }

  #settle() {
    if (!this.#clones.length) return;
    const current = this.#currentSlide();
    const slides = this.#slides;
    let real = current;
    if (current === this.#clones[0]) real = slides[slides.length - 1];
    else if (current === this.#clones[1]) real = slides[0];
    if (real !== current) this.#jumpTo(real);
    this.#anchor = real;
  }
}

if (!customElements.get('s-gallery-loop')) {
  customElements.define('s-gallery-loop', GalleryLoop);
}
